import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, FileSpreadsheet, ReceiptText, TrendingUp, Users, Wallet } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { apiClient } from '../lib/api';
import { formatINR } from '../lib/gst';
import { buildInsights, canUseBasicIntelligence, customerName, downloadCsv, effectiveStatus, invoiceAmount } from '../lib/insights';
import { Button } from '../components/ui/button';

const tabs = ['ledger', 'payments', 'followups', 'exports'];

function MiniStat({ label, value, icon: Icon, tone = 'orange' }) {
  const tones = {
    orange: 'bg-orange-50 text-orange-600',
    green: 'bg-emerald-50 text-emerald-600',
    red: 'bg-red-50 text-red-600',
    slate: 'bg-slate-100 text-slate-600',
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className={`mb-3 flex h-9 w-9 items-center justify-center rounded-lg ${tones[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="text-xs font-black uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-1 font-heading text-2xl font-black text-slate-900">{value}</div>
    </div>
  );
}

function ReportCard({ icon: Icon, title, copy, locked, onExport, exportLabel }) {
  return (
    <div className="relative flex flex-col gap-3 overflow-hidden rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
      {locked && <div className="pointer-events-none absolute inset-0 bg-slate-950/[0.025]" />}
      <div className="flex min-w-0 items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-600">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <h3 className="font-heading text-base font-black text-slate-900">{title}</h3>
          <p className="mt-0.5 text-sm leading-5 text-slate-500">{copy}</p>
        </div>
      </div>
      <Button
        type="button"
        onClick={onExport}
        disabled={locked}
        className="h-9 shrink-0 bg-slate-900 px-5 text-white hover:bg-slate-800 disabled:bg-slate-100 disabled:text-slate-400"
      >
        {exportLabel}
      </Button>
    </div>
  );
}

export default function Sales() {
  const { user, plan } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('ledger');
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === false) navigate('/login?redirect=/sales');
  }, [user, navigate]);

  const load = useCallback(async () => {
    if (!user || user === false) return;
    setLoading(true);
    const [invoiceRes, customerRes, itemRes] = await Promise.allSettled([
      apiClient.get('/invoices'),
      apiClient.get('/customers'),
      apiClient.get('/items'),
    ]);
    setInvoices(invoiceRes.status === 'fulfilled' ? invoiceRes.value.data || [] : []);
    setCustomers(customerRes.status === 'fulfilled' ? customerRes.value.data || [] : []);
    setItems(itemRes.status === 'fulfilled' ? itemRes.value.data || [] : []);
    setLoading(false);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  if (!user || user === false) return null;

  const insights = buildInsights(invoices, customers, items);
  const isBasic = canUseBasicIntelligence(plan);
  const exportGst = () => downloadCsv('billjaldi-gst-summary.csv', [
    ['Taxable sales', 'CGST', 'SGST', 'IGST', 'Total GST'],
    [insights.gst.taxable, insights.gst.cgst, insights.gst.sgst, insights.gst.igst, insights.gst.total],
  ]);
  const exportSales = () => downloadCsv('billjaldi-sales.csv', [
    ['Invoice #', 'Date', 'Customer', 'Status', 'Due Date', 'Total'],
    ...invoices.map((invoice) => [invoice.number, invoice.date, customerName(invoice), effectiveStatus(invoice), invoice.dueDate || '', invoiceAmount(invoice)]),
  ]);
  const exportOutstanding = () => downloadCsv('billjaldi-customer-outstanding.csv', [
    ['Customer', 'Total billed', 'Paid', 'Outstanding', 'Overdue', 'Last invoice'],
    ...insights.customerLedger.map((row) => [row.name, row.billed, row.paid, row.outstanding, row.overdue, row.lastInvoiceDate]),
  ]);
  const exportProfit = () => downloadCsv('billjaldi-profit-summary.csv', [
    ['Metric', 'Value'],
    ['Taxable revenue', insights.taxableRevenue],
    ['Estimated gross profit', insights.grossProfit],
    ['Gross margin %', insights.grossMargin],
    ['Average invoice value', insights.averageInvoiceValue],
  ]);

  return (
    <div className="mx-auto max-w-[1240px] px-4 py-10 sm:px-6 lg:px-8" data-testid="sales-page">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">{t('salesReports')}</h1>
          <p className="mt-1 text-sm text-slate-500">{t('salesReportsCopy')}</p>
        </div>
        <Link to="/history">
          <Button className="bg-slate-900 text-white hover:bg-slate-800">{t('history')}</Button>
        </Link>
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MiniStat label={t('customers')} value={insights.customerLedger.length} icon={Users} tone="slate" />
        <MiniStat label={t('accountsReceived')} value={formatINR(insights.received)} icon={CheckCircle2} tone="green" />
        <MiniStat label={t('outstanding')} value={formatINR(insights.receivable)} icon={Wallet} tone="orange" />
        <MiniStat label={t('overdue')} value={formatINR(insights.overdue)} icon={AlertTriangle} tone={insights.overdue ? 'red' : 'slate'} />
      </div>

      <div className="mb-5 flex flex-wrap gap-2">
        {tabs.map((tab) => (
          <button
            key={tab}
            type="button"
            disabled={!isBasic}
            onClick={() => {
              if (!isBasic) return;
              setActiveTab(tab);
            }}
            className={`relative h-10 overflow-hidden rounded-full px-4 text-sm font-black transition-colors ${activeTab === tab ? 'bg-slate-900 text-white' : 'border border-slate-200 bg-white text-slate-600 hover:text-slate-900'} ${!isBasic ? 'cursor-not-allowed text-slate-400 hover:text-slate-400' : ''}`}
            data-testid={`sales-tab-${tab}`}
          >
            {!isBasic && <span className="absolute inset-0 bg-slate-950/[0.035]" />}
            {t(tab)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-400">{t('loading')}</div>
      ) : activeTab === 'ledger' ? (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 font-bold">{t('customer')}</th>
                <th className="px-4 py-3 text-right font-bold">{t('totalBilled')}</th>
                <th className="px-4 py-3 text-right font-bold">{t('paid')}</th>
                <th className="px-4 py-3 text-right font-bold">{t('outstanding')}</th>
                <th className="px-4 py-3 text-right font-bold">{t('overdue')}</th>
                <th className="px-4 py-3 font-bold">{t('lastInvoice')}</th>
              </tr>
            </thead>
            <tbody className={!isBasic ? 'opacity-45' : ''}>
              {insights.customerLedger.map((row) => (
                <tr key={row.name} className="border-b border-slate-100">
                  <td className="px-4 py-3 font-bold text-slate-900">{row.name}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatINR(row.billed)}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-700">{formatINR(row.paid)}</td>
                  <td className="px-4 py-3 text-right font-mono text-orange-700">{formatINR(row.outstanding)}</td>
                  <td className="px-4 py-3 text-right font-mono text-red-700">{formatINR(row.overdue)}</td>
                  <td className="px-4 py-3 text-slate-600">{row.lastInvoiceDate || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!insights.customerLedger.length && <div className="p-8 text-center text-sm text-slate-400">{t('noInvoicesYet')}</div>}
        </div>
      ) : activeTab === 'payments' ? (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
            <h2 className="font-heading text-lg font-black text-slate-900">{t('paymentQueue')}</h2>
            <p className="mt-0.5 text-xs font-semibold text-slate-500">{t('paymentQueueCopy')}</p>
          </div>
          <table className="w-full min-w-[840px] text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 font-bold">{t('invoiceHash')}</th>
                <th className="px-4 py-3 font-bold">{t('customer')}</th>
                <th className="px-4 py-3 font-bold">{t('status')}</th>
                <th className="px-4 py-3 font-bold">{t('dueDate')}</th>
                <th className="px-4 py-3 text-right font-bold">{t('amount')}</th>
              </tr>
            </thead>
            <tbody>
              {[...insights.unpaidInvoices, ...insights.paidInvoices].map((invoice) => (
                <tr key={invoice.id || invoice.bj_key || invoice.number} className="border-b border-slate-100">
                  <td className="px-4 py-3 font-mono font-bold text-slate-900">{invoice.number}</td>
                  <td className="px-4 py-3 text-slate-900">{customerName(invoice)}</td>
                  <td className="px-4 py-3 font-bold uppercase text-slate-600">{t(effectiveStatus(invoice))}</td>
                  <td className="px-4 py-3 text-slate-600">{invoice.dueDate || '-'}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold">{formatINR(invoiceAmount(invoice))}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!insights.unpaidInvoices.length && !insights.paidInvoices.length && <div className="p-8 text-center text-sm text-slate-400">{t('noInvoicesYet')}</div>}
        </div>
      ) : activeTab === 'followups' ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {insights.needsFollowup.map((invoice) => (
            <div key={invoice.id || invoice.bj_key || invoice.number} className="rounded-xl border border-red-100 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-start justify-between gap-4">
                <div>
                  <div className="font-mono text-sm font-black text-slate-900">{invoice.number}</div>
                  <div className="mt-1 text-sm font-bold text-slate-700">{customerName(invoice)}</div>
                </div>
                <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-black uppercase tracking-wider text-red-700">{t('overdue')}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="text-[10px] font-black uppercase tracking-wider text-slate-400">{t('dueDate')}</div>
                  <div className="mt-1 font-bold text-slate-900">{invoice.dueDate || '-'}</div>
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="text-[10px] font-black uppercase tracking-wider text-slate-400">{t('amount')}</div>
                  <div className="mt-1 font-mono font-bold text-slate-900">{formatINR(invoiceAmount(invoice))}</div>
                </div>
              </div>
              <Link to={`/history?status=overdue`} className="mt-4 inline-flex text-sm font-black text-orange-600 hover:text-orange-700">
                {t('openInHistory')}
              </Link>
            </div>
          ))}
          {!insights.needsFollowup.length && (
            <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-400 shadow-sm md:col-span-2 xl:col-span-3">
              {t('noFollowups')}
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-xs font-black uppercase tracking-wider text-slate-400">{t('invoiceValue')}</div>
              <div className="mt-1 font-heading text-2xl font-black text-slate-900">{formatINR(insights.invoiceValue)}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-xs font-black uppercase tracking-wider text-slate-400">{t('estimatedGst')}</div>
              <div className="mt-1 font-heading text-2xl font-black text-slate-900">{formatINR(insights.gst.total)}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-xs font-black uppercase tracking-wider text-slate-400">{t('outstanding')}</div>
              <div className="mt-1 font-heading text-2xl font-black text-slate-900">{formatINR(insights.receivable)}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-xs font-black uppercase tracking-wider text-slate-400">{t('estimatedProfit')}</div>
              <div className="mt-1 font-heading text-2xl font-black text-slate-900">{formatINR(insights.grossProfit)}</div>
            </div>
          </div>
          <div className="grid gap-3">
            <ReportCard icon={ReceiptText} title={t('gstSummaryExport')} copy="CGST, SGST, IGST, taxable sales, and total GST." locked={!isBasic} onExport={exportGst} exportLabel={t('export')} />
            <ReportCard icon={FileSpreadsheet} title={t('salesCsvExport')} copy="Invoice-level sales export for review and reconciliation." locked={!isBasic} onExport={exportSales} exportLabel={t('export')} />
            <ReportCard icon={Users} title={t('customerOutstandingExport')} copy="Customer-wise billed, paid, outstanding, and overdue balances." locked={!isBasic} onExport={exportOutstanding} exportLabel={t('export')} />
            <ReportCard icon={TrendingUp} title={t('profitSummary')} copy="Taxable revenue, estimated gross profit, margin, and average invoice value." locked={!isBasic} onExport={exportProfit} exportLabel={t('export')} />
          </div>
        </div>
      )}
    </div>
  );
}
